/**
 * Hosted checkout redirect for Shopify storefronts.
 *
 * This asset runs in the Shopify theme only. It never holds Whop or Shopify
 * Admin secrets; it only reads the public Shopify cart and forwards a compact
 * cart payload to the hosted checkout app.
 */
(function () {
  var CONFIG = window.ML_WHOP_CHECKOUT_CONFIG || window.WHOP_CHECKOUT_CONFIG || {};
  var CHECKOUT_BASE_URL = String(
    CONFIG.checkoutUrl ||
    CONFIG.checkoutBaseUrl ||
    CONFIG.hostedCheckoutUrl ||
    ''
  ).replace(/\/$/, '');
  if (CONFIG.enabled === false || !CHECKOUT_BASE_URL) return;
  var isOpening = false;
  var openingStartedAt = 0;
  var CART_ATTEMPTS = 12;
  var CHECKOUT_VERSION = '20260716-shopify-cart-discounts-1';

  var CHECKOUT_SELECTORS = [
    'button[name="checkout"]',
    'button[type="submit"][name="checkout"]',
    'input[name="checkout"]',
    'input[type="submit"][name="checkout"]',
    'a[href="/checkout"]',
    'a[href^="/checkout?"]',
    '#checkout',
    '.cart__checkout-button',
    '[form="cart"][name="checkout"]',
    '[data-cart-checkout]',
    '[data-checkout-button]',
    '[data-testid*="checkout" i]',
    '[aria-label*="checkout" i]',
    '[aria-label*="afrekenen" i]',
    '[aria-label*="pagamento" i]',
    '[aria-label*="cassa" i]'
  ].join(',');

  function hasText(value, needle) {
    return String(value || '').toLowerCase().indexOf(needle) !== -1;
  }

  function findCheckoutTextTarget(el) {
    if (!el || !el.closest) return null;
    var target = el.closest('button, input, a');
    if (!target) return null;
    var text = String(target.innerText || target.value || target.getAttribute('aria-label') || '').toLowerCase();
    var href = target.getAttribute && target.getAttribute('href');
    var name = target.getAttribute && target.getAttribute('name');
    var id = target.getAttribute && target.getAttribute('id');
    if (name === 'checkout' || id === 'checkout') return target;
    if (href === '/checkout' || String(href || '').indexOf('/checkout?') === 0) return target;
    if (
      (
        hasText(text, 'checkout') ||
        hasText(text, 'afrekenen') ||
        hasText(text, 'secure checkout') ||
        hasText(text, 'check out') ||
        hasText(text, 'pagamento') ||
        hasText(text, 'cassa')
      ) &&
      target.closest('form, .cart, .drawer, [class*="cart"], [id*="cart"]')
    ) {
      return target;
    }
    return null;
  }

  function normalizeImage(url) {
    if (!url) return null;
    if (String(url).indexOf('//') === 0) return window.location.protocol + String(url);
    return String(url);
  }

  function centsFromMoney(text) {
    var cleaned = String(text || '').replace(/[^0-9.,]/g, '').replace(',', '.');
    var num = parseFloat(cleaned || '0');
    return Math.round(num * 100);
  }

  function getCartFromDom() {
    var rows = Array.prototype.slice.call(document.querySelectorAll('.m-cart-item, [data-cart-item]'));
    var items = rows.map(function (row, idx) {
      var titleEl = row.querySelector('a[href*="/products/"], .m-cart-item__title, [data-cart-item-title]');
      var qtyEl = row.querySelector('input[type="number"], input[name*="quantity"]');
      var imageEl = row.querySelector('img');
      var moneyEls = Array.prototype.slice.call(row.querySelectorAll('*')).filter(function (el) {
        return /(?:\$|€|£|USD|EUR|GBP)\s*\d/i.test(el.textContent || '');
      });
      var priceText = moneyEls.length ? moneyEls[moneyEls.length - 1].textContent : '';
      var quantity = qtyEl ? parseInt(qtyEl.value || '1', 10) : 1;
      var linePrice = centsFromMoney(priceText);
      return {
        variant_id: null,
        quantity: quantity || 1,
        title: (titleEl && titleEl.textContent || 'Cart item ' + (idx + 1)).trim(),
        sku: null,
        price: quantity ? Math.round(linePrice / quantity) : linePrice,
        final_line_price: linePrice,
        image: imageEl ? imageEl.currentSrc || imageEl.src : null
      };
    }).filter(function (item) { return item.final_line_price > 0; });

    var totalPrice = items.reduce(function (sum, item) { return sum + item.final_line_price; }, 0);
    if (!items.length || !totalPrice) throw new Error('Could not read cart from page');

    return {
      currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || CONFIG.currency || 'USD',
      item_count: items.reduce(function (sum, item) { return sum + item.quantity; }, 0),
      total_price: totalPrice,
      items: items
    };
  }

  function sleep(ms) {
    return new Promise(function (resolve) {
      window.setTimeout(resolve, ms);
    });
  }

  function cartHasItems(cart) {
    return Boolean(
      cart &&
      Number(cart.item_count || 0) > 0 &&
      Array.isArray(cart.items) &&
      cart.items.length > 0 &&
      Number(cart.total_price || 0) > 0
    );
  }

  function readShopifyCart() {
    var controller = window.AbortController ? new AbortController() : null;
    var timeout = controller ? window.setTimeout(function () {
      controller.abort();
    }, 2500) : null;
    return fetch('/cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      signal: controller ? controller.signal : undefined
    }).then(function (r) {
      if (timeout) window.clearTimeout(timeout);
      if (!r.ok) throw new Error('Could not read Shopify cart');
      return r.json();
    }).catch(function (error) {
      if (timeout) window.clearTimeout(timeout);
      throw error;
    });
  }

  function getCart(attempt) {
    attempt = attempt || 0;
    return readShopifyCart().then(function (cart) {
      if (cartHasItems(cart)) return cart;
      if (attempt < CART_ATTEMPTS) {
        return sleep(220 + attempt * 260).then(function () {
          return getCart(attempt + 1);
        });
      }
      return getCartFromDom();
    }).catch(function (error) {
      if (attempt < CART_ATTEMPTS) {
        return sleep(220 + attempt * 260).then(function () {
          return getCart(attempt + 1);
        });
      }
      try {
        return getCartFromDom();
      } catch (domError) {
        throw error || domError;
      }
    });
  }

  function normalizeVariantOptions(item) {
    var optionsWithValues = Array.isArray(item.options_with_values) ? item.options_with_values : [];
    var options = optionsWithValues.map(function (option) {
      var name = String(option.name || option.title || '').trim();
      var value = String(option.value || '').trim();
      if (!name || !value || value.toLowerCase() === 'default title') return null;
      return { name: name, value: value };
    }).filter(Boolean);

    if (options.length) return options;

    var variantOptions = Array.isArray(item.variant_options) ? item.variant_options : [];
    options = variantOptions.map(function (value, index) {
      value = String(value || '').trim();
      if (!value || value.toLowerCase() === 'default title') return null;
      return { name: 'Option ' + (index + 1), value: value };
    }).filter(Boolean);

    if (options.length) return options;

    var variantTitle = String(item.variant_title || '').trim();
    if (variantTitle && variantTitle.toLowerCase() !== 'default title') {
      return variantTitle.split('/').map(function (value, index) {
        value = String(value || '').trim();
        if (!value) return null;
        return { name: 'Option ' + (index + 1), value: value };
      }).filter(Boolean);
    }

    return [];
  }

  function toCheckoutPayload(cart) {
    var items = (cart.items || []).map(function (item) {
      return {
        variantId: item.variant_id ? 'gid://shopify/ProductVariant/' + item.variant_id : null,
        title: item.product_title || item.title || 'Cart item',
        quantity: Number(item.quantity || 1),
        priceCents: Number(item.price || 0),
        finalLinePriceCents: Number(item.final_line_price || 0),
        finalPriceCents: Number(item.final_price || 0),
        image: normalizeImage(item.image),
        sku: item.sku || null,
        variantTitle: item.variant_title || null,
        options: normalizeVariantOptions(item)
      };
    }).filter(function (item) {
      return item.quantity > 0 && item.priceCents > 0;
    });

    return {
      currency: (cart.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || CONFIG.currency || 'USD').toUpperCase(),
      totalCents: Number(cart.total_price || items.reduce(function (sum, item) {
        return sum + item.priceCents * item.quantity;
      }, 0)),
      originalTotalCents: Number(cart.original_total_price || cart.items_subtotal_price || items.reduce(function (sum, item) {
        return sum + item.priceCents * item.quantity;
      }, 0)),
      shopifyDiscountCents: Number(cart.total_discount || 0),
      shopifyDiscounts: []
        .concat(cart.cart_level_discount_applications || [])
        .concat((cart.items || []).reduce(function (all, item) {
          return all.concat(item.discount_allocations || []);
        }, []))
        .map(function (discount) {
          return {
            title: String(discount.title || discount.discount_application?.title || '').slice(0, 80),
            amountCents: Number(discount.total_allocated_amount || discount.amount || 0)
          };
        })
        .filter(function (discount) { return discount.title || discount.amountCents > 0; })
        .slice(0, 10),
      itemCount: Number(cart.item_count || items.reduce(function (sum, item) {
        return sum + item.quantity;
      }, 0)),
      lineItems: items
    };
  }

  function base64UrlEncode(value) {
    var json = JSON.stringify(value);
    var encoded = btoa(unescape(encodeURIComponent(json)));
    encoded = encoded.split('+').join('-').split('/').join('_');
    while (encoded.charAt(encoded.length - 1) === '=') encoded = encoded.slice(0, -1);
    return encoded;
  }

  function redirectToHostedCheckout(cart) {
    var payload = toCheckoutPayload(cart);
    if (!payload.lineItems.length || !payload.totalCents) throw new Error('Cart is empty');
    var url = CHECKOUT_BASE_URL + '/checkout?cart=' + encodeURIComponent(base64UrlEncode(payload));
    window.location.assign(url);
  }

  function setButtonBusy(clicked, busy) {
    if (!clicked) return;
    if (busy) {
      clicked.setAttribute('aria-busy', 'true');
      return;
    }
    clicked.removeAttribute('aria-busy');
    if ((clicked.tagName === 'BUTTON' || clicked.tagName === 'INPUT') && clicked.dataset.hostedCheckoutWasDisabled !== 'true') {
      clicked.disabled = false;
    }
  }

  function fallbackToShopifyCheckout(reason, clicked) {
    isOpening = false;
    openingStartedAt = 0;
    console.error('Hosted checkout failed:', reason);
    setButtonBusy(clicked, false);
    window.location.assign('/checkout');
  }

  function resetOpeningState() {
    isOpening = false;
    openingStartedAt = 0;
    healCheckoutButtons();
  }

  function startHostedCheckout(evt) {
    if (evt && typeof evt.preventDefault === 'function') evt.preventDefault();
    if (evt && typeof evt.stopPropagation === 'function') evt.stopPropagation();
    if (evt && typeof evt.stopImmediatePropagation === 'function') evt.stopImmediatePropagation();

    if (isOpening && openingStartedAt && Date.now() - openingStartedAt > 3000) {
      resetOpeningState();
    }
    if (isOpening) return false;
    isOpening = true;
    openingStartedAt = Date.now();
    var clicked = null;
    if (evt && evt.target && evt.target.closest) {
      clicked = evt.target.closest('button, input, a');
      if (clicked) {
        clicked.dataset.hostedCheckoutWasDisabled = clicked.disabled ? 'true' : 'false';
        setButtonBusy(clicked, true);
      }
    }

    getCart()
      .then(redirectToHostedCheckout)
      .catch(function (reason) {
        fallbackToShopifyCheckout(reason, clicked);
      });

    return false;
  }

  function buttonLooksLikeReadyCheckout(el) {
    if (!el) return false;
    var text = String(el.innerText || el.value || el.getAttribute('aria-label') || '').toLowerCase();
    var hasCheckoutText =
      hasText(text, 'checkout') ||
      hasText(text, 'check out') ||
      hasText(text, 'afrekenen') ||
      hasText(text, 'pagamento') ||
      hasText(text, 'cassa') ||
      el.getAttribute('name') === 'checkout';
    var hasMoney = /(?:£|€|\$|gbp|eur|usd)\s*\d/i.test(text) || Boolean(el.querySelector && el.querySelector('[ymq-cart-money], .ymq-cart-app-total'));
    var cartLooksReady = Boolean(
      document.querySelector('.ymq-cart-app-checkout-button, .ymq-cart-app-total, .cart-item, .m-cart-item, [data-cart-item]')
    );
    return Boolean(hasCheckoutText && (hasMoney || cartLooksReady));
  }

  function healCheckoutButtons() {
    document.querySelectorAll(CHECKOUT_SELECTORS).forEach(function (el) {
      if (buttonLooksLikeReadyCheckout(el) && (el.disabled || el.getAttribute('aria-busy') === 'true')) {
        el.disabled = false;
        el.removeAttribute('aria-busy');
      }
    });
  }

  function handleCheckoutClick(evt) {
    var target = evt.target && evt.target.closest ? evt.target.closest(CHECKOUT_SELECTORS) || findCheckoutTextTarget(evt.target) : null;
    if (!target) return;
    return startHostedCheckout(evt);
  }

  function handleCartSubmit(evt) {
    var submitter = evt.submitter || document.activeElement;
    var isCheckoutSubmit = submitter && (
      (submitter.matches && submitter.matches(CHECKOUT_SELECTORS)) ||
      findCheckoutTextTarget(submitter)
    );
    var formLooksLikeCart = evt.target && evt.target.matches && evt.target.matches('form[action="/cart"], form[action$="/cart"]');
    if (!isCheckoutSubmit && (!formLooksLikeCart || !findCheckoutTextTarget(submitter))) return;
    return startHostedCheckout(evt);
  }

  function bindCheckoutButtons() {
    document.querySelectorAll(CHECKOUT_SELECTORS).forEach(function (el) {
      if (buttonLooksLikeReadyCheckout(el) && el.disabled) {
        el.disabled = false;
        el.removeAttribute('aria-busy');
      }
      if (el.dataset.hostedCheckoutBound === 'true') return;
      el.dataset.hostedCheckoutBound = 'true';
      if (el.tagName === 'BUTTON' || el.tagName === 'INPUT') {
        el.setAttribute('type', 'button');
      }
      if (el.tagName === 'A') {
        el.setAttribute('href', '#hosted-checkout');
      }
      el.addEventListener('click', startHostedCheckout, true);
    });
  }

  function initHostedCheckoutOverride() {
    bindCheckoutButtons();
    healCheckoutButtons();
    var observer = new MutationObserver(bindCheckoutButtons);
    observer.observe(document.documentElement, { childList: true, subtree: true });
    window.setInterval(healCheckoutButtons, 1000);
  }

  window.__startHostedCheckout = startHostedCheckout;
  window.__whopHostedCheckoutVersion = CHECKOUT_VERSION;
  window.__resetHostedCheckout = resetOpeningState;
  document.addEventListener('click', handleCheckoutClick, true);
  document.addEventListener('submit', handleCartSubmit, true);
  window.addEventListener('pageshow', resetOpeningState);
  window.addEventListener('popstate', resetOpeningState);
  window.addEventListener('focus', resetOpeningState);
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) resetOpeningState();
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHostedCheckoutOverride);
  } else {
    initHostedCheckoutOverride();
  }
})();